import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { MutationConfig } from '@/lib/react-query';
import { api, extractData } from '@/lib/treaty';
import { invalidateResourceQueries } from '@/features/document-archives/core/query/invalidateUtils';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Estate Documents
type UploadEstateDocumentOptions = {
  mutationConfig?: MutationConfig<typeof uploadEstateDocument>;
};

type UploadEstateDocumentType = {
  document: File;
  name?: string;
  estateId: string;
};

const uploadEstateDocument = ({ document, name, estateId }: UploadEstateDocumentType) => {
  return extractData(api.estates({ id: estateId }).documents.post({ name, document }));
};

export const useUploadDocument = ({ mutationConfig }: UploadEstateDocumentOptions = {}) => {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: uploadEstateDocument,
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      if ((variables as { skipInvalidate?: boolean })?.skipInvalidate) {
        await mutationConfig?.onSuccess?.(data, variables, context);
        return;
      }
      try {
        await invalidateResourceQueries(qc, 'estate', variables.estateId);
      } finally {
        toast.success(t('file-uploaded-success'));
        await mutationConfig?.onSuccess?.(data, variables, context);
      }
    },
  });
};

// Project Documents
type UploadProjectDocumentOptions = {
  mutationConfig?: MutationConfig<typeof uploadProjectDocument>;
};

type UploadProjectDocumentType = {
  document: File;
  name?: string;
  projectId: string;
};

const uploadProjectDocument = ({ document, name, projectId }: UploadProjectDocumentType) => {
  return extractData(api.projects({ id: projectId }).documents.post({ name, document }));
};

export const useUploadProjectDocument = ({ mutationConfig }: UploadProjectDocumentOptions = {}) => {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: uploadProjectDocument,
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      if ((variables as { skipInvalidate?: boolean })?.skipInvalidate) {
        await mutationConfig?.onSuccess?.(data, variables, context);
        return;
      }
      try {
        await invalidateResourceQueries(qc, 'project', variables.projectId);
      } finally {
        toast.success(t('file-uploaded-success'));
        await mutationConfig?.onSuccess?.(data, variables, context);
      }
    },
  });
};
